/**
 * Loads the DC-12 checklists list: one request per page. The lease banner + tab share a single
 * lease-scoped call (`{ leaseId }`); the Checklists page pages with `loadMore` (nextCursor).
 */

import { useInfiniteQuery } from '@tanstack/react-query';
import { listChecklists, type ChecklistListParams, type ChecklistRow } from './condition.api';

export interface UseChecklistsResult {
  data: ChecklistRow[] | undefined;
  status: 'loading' | 'error' | 'ready';
  retry: () => void;
  /** The server's lease scan hit its cap: the list may be incomplete (BR-34). */
  truncated: boolean;
  hasMore: boolean;
  loadMore: () => void;
  isLoadingMore: boolean;
}

export function useChecklists(query: Omit<ChecklistListParams, 'cursor'>, options: { enabled?: boolean } = {}): UseChecklistsResult {
  const result = useInfiniteQuery({
    queryKey: ['condition-checklists', query],
    queryFn: ({ pageParam }) => listChecklists({ ...query, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    retry: false,
    enabled: options.enabled ?? true,
  });

  return {
    data: result.data?.pages.flatMap((p) => p.items),
    status: result.isError && !result.data ? 'error' : result.data ? 'ready' : 'loading',
    retry: () => {
      void result.refetch();
    },
    truncated: result.data?.pages.some((p) => p.truncated) ?? false,
    hasMore: result.hasNextPage,
    loadMore: () => {
      void result.fetchNextPage();
    },
    isLoadingMore: result.isFetchingNextPage,
  };
}
